import React, { ReactElement, useState } from "react"
import { CarrotPlan } from "../../types/CarrotPlan"
import { useEmployeeSettings } from "./hooks/employeeSettings"
import useCarrotPlans from "./hooks/useCarrotPlans"
import ErrorWrapper from "../ErrorWrapper"
import useEmployee from "../employees/hooks/useEmployee"
import useRoutingInfo from "components/employees/hooks/useRoutingInfo"
import {
  Typography,
  Button,
  Box,
  Stack,
  Tooltip,
  ThemeProvider,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
  InfoIcon
} from "@carrotfertility/carotene-core"
import { useGetAllMedicalQuestionsSkipped } from "./hooks/useGetAllMedicalQuestionsSkipped"
import { CenteredLoadingIndicator } from "../../utils/CenteredLoadingIndicator"
import { RoutingInfo } from "types/RoutingInfo"
import { useGetPastRoutingInfo } from "components/employees/hooks/useGetPastRoutingInfo"
import { getMatchingRoutingInfoForCarrotPlan } from "./utils/getMatchingRoutingInfoForCarrotPlan"
import { PlanEntry, RoutingAnswers } from "./PlanTableRows"
import useAdminUser from "components/hooks/useAdminUser"
import DeleteTestCarrotPlanDialog from "./DeleteTestCarrotPlanDialog"

interface PlansListProps {
  employeeId: number
}

interface HeaderProps {
  carrotPlanCreationEnabled: boolean
  isSettingsLoading: boolean
  getTooltipLabel: () => string
  employeeId: number
  canDeleteTestCarrotPlans: boolean
  onDeleteTestPlanClick: () => void
}

interface PlansTableProps {
  employeeId: number
  plansList: CarrotPlan[] | undefined
  carrotPlansRoutingCompleted: Date | undefined
  allMedicalQuestionsSkipped: boolean
  routingInfo: RoutingInfo | undefined
  pastRoutingInfo: RoutingInfo[] | undefined
  getPlansListError: any
}

const InfoTooltip = ({ message }: { message: string }) => {
  const [open, setOpen] = React.useState(false)

  return (
    <Tooltip open={open} title={message} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <IconButton>
        <InfoIcon />
      </IconButton>
    </Tooltip>
  )
}

const Header = ({
  carrotPlanCreationEnabled,
  isSettingsLoading,
  getTooltipLabel,
  employeeId,
  canDeleteTestCarrotPlans,
  onDeleteTestPlanClick
}: HeaderProps) => (
  <Stack direction="row" alignItems="center" width="100%">
    <Box>
      <Typography variant={"h4"}>All Carrot Plans</Typography>
    </Box>
    <Box flexGrow={1} />
    <Box>
      {!isSettingsLoading && (
        <Stack direction="row" spacing={1} alignItems="center">
          {!carrotPlanCreationEnabled && <InfoTooltip message={getTooltipLabel()} />}
          <Button
            variant={"contained"}
            disabled={!carrotPlanCreationEnabled}
            onClick={() => window.open(`/employees/${employeeId}/carrot-plans/create-plan-v2`, "_blank")}
          >
            Create Carrot Plan
          </Button>
          {canDeleteTestCarrotPlans && carrotPlanCreationEnabled && (
            <Button
              variant={"contained"}
              onClick={onDeleteTestPlanClick}
              sx={{ bgcolor: "#b71c1c", color: "#fff", border: "2px solid #000", "&:hover": { bgcolor: "#7f0000" } }}
            >
              Delete Test Plan
            </Button>
          )}
        </Stack>
      )}
    </Box>
  </Stack>
)

const PlansTable = ({
  employeeId,
  plansList,
  carrotPlansRoutingCompleted,
  allMedicalQuestionsSkipped,
  routingInfo,
  pastRoutingInfo,
  getPlansListError
}: PlansTableProps) => (
  <Table>
    <TableHead sx={{ backgroundColor: (theme) => theme.palette.background.hover }}>
      <TableRow>
        <TableCell>Date</TableCell>
        <TableCell>Source</TableCell>
        <TableCell>Published By</TableCell>
        <TableCell>Tags</TableCell>
        <TableCell>Journey / Phase</TableCell>
        <TableCell>Actions</TableCell>
      </TableRow>
    </TableHead>

    <TableBody>
      {carrotPlansRoutingCompleted && routingInfo && (
        <RoutingAnswers
          key="latest-answers"
          employeeId={employeeId}
          routingInfo={routingInfo}
          allMedicalQuestionsSkipped={allMedicalQuestionsSkipped}
        />
      )}

      <ErrorWrapper error={getPlansListError}>
        {plansList?.map(
          (plan): ReactElement => {
            const matchingRoutingInfo = getMatchingRoutingInfoForCarrotPlan({
              carrotPlan: plan,
              mostRecentRoutingInfo: routingInfo,
              pastRoutingInfo
            })
            return (
              <PlanEntry
                key={plan.carrotPlanId}
                employeeId={employeeId}
                plan={plan}
                matchingRoutingInfo={matchingRoutingInfo}
              />
            )
          }
        )}
      </ErrorWrapper>
    </TableBody>
  </Table>
)

export default function CarrotPlanList({ employeeId }: PlansListProps): ReactElement {
  const { data: plansList, isLoading: isCarrotPlansListLoading, error: getPlansListError } = useCarrotPlans(employeeId)
  const { data: employeeSettings, isLoading: isSettingsLoading, error: fetchError } = useEmployeeSettings(employeeId)
  const { data: employeeData } = useEmployee(employeeId.toString())
  const { data: routingInfo, isLoading: isRoutingInfoLoading } = useRoutingInfo(employeeId)
  const { data: pastRoutingInfo, isLoading: isRoutingPastLoading } = useGetPastRoutingInfo(employeeId)
  const { data: adminUser } = useAdminUser()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const allMedicalQuestionsSkipped = useGetAllMedicalQuestionsSkipped(employeeId)
  const employee = employeeData?.employee
  const isRegistered = !!employee?.registrationCompletedDate
  const hasCarrotPlanEmployeeSettings = !!employeeSettings?.carrotPlanEmployeeSettingId

  const carrotPlanCreationEnabled = isRegistered && hasCarrotPlanEmployeeSettings && !fetchError
  const isLoading = isCarrotPlansListLoading || isRoutingInfoLoading || isSettingsLoading || isRoutingPastLoading

  const getTooltipLabel = (): string => {
    if (!isRegistered) {
      return "A Carrot Plan cannot be created because the member hasn't registered"
    }
    if (!hasCarrotPlanEmployeeSettings) {
      return "A Carrot Plan cannot be created because the member hasn't completed the routing flow"
    }
    return "A Carrot Plan cannot be created. Refresh and try again. If problems still occur, reach out to #eng-support"
  }

  const theme = useTheme()
  return (
    <ThemeProvider>
      <Box
        // This styling to needed to widen the CarrotPlanList component without having to adjust the styling of the other Carrot Plan related pages
        sx={{
          width: "95vw",
          marginLeft: "calc(-50vw + 525px)",
          "@media (max-width: 990px)": {
            width: "95vw",
            marginLeft: "30px"
          }
        }}
        paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.xxxl)}
      >
        <Stack direction="column" spacing={theme.spacing(theme.tokens.spacing.xl)}>
          <Header
            carrotPlanCreationEnabled={carrotPlanCreationEnabled}
            isSettingsLoading={isSettingsLoading}
            getTooltipLabel={getTooltipLabel}
            employeeId={employeeId}
            canDeleteTestCarrotPlans={!!adminUser?.canDeleteTestCarrotPlans}
            onDeleteTestPlanClick={() => setDeleteDialogOpen(true)}
          />

          {isLoading ? (
            <CenteredLoadingIndicator />
          ) : (
            <PlansTable
              employeeId={employeeId}
              plansList={plansList}
              carrotPlansRoutingCompleted={employee?.carrotPlansRoutingCompleted}
              allMedicalQuestionsSkipped={allMedicalQuestionsSkipped}
              routingInfo={routingInfo}
              pastRoutingInfo={pastRoutingInfo}
              getPlansListError={getPlansListError}
            />
          )}
        </Stack>

        <DeleteTestCarrotPlanDialog
          employeeId={employeeId}
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onSuccess={() => setDeleteDialogOpen(false)}
        />
      </Box>
    </ThemeProvider>
  )
}
